import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import deepEqual from 'fast-deep-equal';
import { OnboardingNavbar } from '../OnboardingNavbar/OnboardingNavbar';
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress';
import { Button, Input, Select } from '../../ui';
import { IconTrash, IconCircledPlus } from '../../ui/Icons';
import { OnboardingActions } from '../OnboardingActions/OnboardingActions';
import { selectUpdateConfigLoading } from '../../../redux/config/selectors';
import {
  OnboardingHeaderGroup,
  OnboardingContent,
  OnboardingContentGroup,
  OnboardingStepWrapper,
  OnboardingContentInfo,
  OnboardingContentTitle,
  OnboardingContentGroupText,
  OnboardingContentGroupLink,
  OnboardingFieldWrapper,
  OnboardingFieldErrorText,
  OnboardingUserListRow,
  OnboardingUserListHeader,
  OnboardingUserListHeaderItem
} from '../../ui/Layout/OnboardingLayout';
import { Heading2, Heading3 } from '../../ui/Typography/Heading';
import { logout } from '../../../redux/authentication/slice';
import { updateConfigOnboardingSettings } from '../../../redux/config/slice';
import { selectCurrentOrganization, selectCurrentUser } from '../../../redux/authentication/selectors';
import { selectUsers, selectUserRoleOptions, selectBulkEditUsersLoading } from '../../../redux/users/selectors';
import ConfirmLeaveModal from '../../ConfirmLeaveModal/ConfirmLeaveModal';

import { getUsers, bulkEditUsers, getUserRoles } from '../../../redux/users/slice';
import { isHiddenOqioUser } from '../../../helpers/utility';
import { ONBOARDING_STEPS } from '../../../consts/consts';
import {
  EMAIL_FORMAT_REGEX,
  normalizeEmail,
  trimValue,
  getFieldTouchKey,
  touchAllFields
} from '../../../helpers/onboardingValidation.js';

const ONBOARDING_HELP_CENTER_URL =
  'https://help.oqio.de/de/articles/13634593-rollen-und-rechtemanagement?utm_source=onboarding&utm_medium=inproduct';

const OnboardingStepUsersComponent = ({
  users,
  currentUser,
  updateConfigOnboardingSettings,
  getUsers,
  logout,
  bulkEditUsers,
  getUserRoles,
  userOrganization,
  userRoleOptions,
  updateConfigLoading,
  bulkEditUsersLoading
}) => {
  const [localUsers, setLocalUsers] = useState([]);
  const [initialUsersState, setInitialUsersState] = useState([]);
  const [touchedFields, setTouchedFields] = useState({});
  const [userIdPendingDelete, setUserIdPendingDelete] = useState(null);

  useEffect(() => {
    if (userOrganization?.type) {
      getUserRoles({ organisationType: userOrganization.type });
    }
    if (currentUser?.organization_id) {
      getUsers({ organizationId: currentUser.organization_id });
    }
  }, [userOrganization, currentUser, getUserRoles, getUsers]);

  useEffect(() => {
    if (users && currentUser) {
      const existingUsers = users
        .filter((user) => !isHiddenOqioUser(user))
        .filter((user) => user.organization_id === currentUser.organization_id)
        .map((user) => ({ ...user }));

      setLocalUsers(existingUsers);
      setInitialUsersState(existingUsers);
    }
  }, [users, currentUser]);

  const isLoading = updateConfigLoading || bulkEditUsersLoading;

  const isFieldTouched = (userId, fieldName) => touchedFields[getFieldTouchKey(userId, fieldName)];

  const handleFieldBlur = (userId, fieldName) => {
    setTouchedFields((prev) => ({
      ...prev,
      [getFieldTouchKey(userId, fieldName)]: true
    }));
  };

  const getEmailDuplicateCount = (email) => {
    const normalizedEmail = normalizeEmail(email);

    if (!normalizedEmail) {
      return 0;
    }

    return localUsers.filter((user) => normalizeEmail(user.email) === normalizedEmail).length;
  };

  const getUserFieldErrorMessage = (user, fieldName, requireTouched = true) => {
    if (requireTouched && !isFieldTouched(user.id, fieldName)) {
      return '';
    }

    const trimmedFirstName = trimValue(user.first_name);
    const trimmedLastName = trimValue(user.last_name);
    const trimmedEmail = trimValue(user.email);
    const normalizedEmail = normalizeEmail(user.email);

    if (fieldName === 'first_name' && !trimmedFirstName) {
      return '↑ Pflichtfeld';
    }

    if (fieldName === 'last_name' && !trimmedLastName) {
      return '↑ Pflichtfeld';
    }

    if (fieldName === 'role' && !user.role) {
      return '↑ Pflichtfeld';
    }

    if (fieldName === 'email') {
      if (!trimmedEmail) {
        return '↑ Pflichtfeld';
      }

      if (!EMAIL_FORMAT_REGEX.test(normalizedEmail)) {
        return '↑ Die Eingabe ist keine gültige E-Mail';
      }

      if (getEmailDuplicateCount(user.email) > 1) {
        return '↑ Diese E-Mail-Adresse wird bereits verwendet';
      }

      if (isHiddenOqioUser({ ...user, email: normalizedEmail })) {
        return '↑ Diese E-Mail-Adresse kann nicht verwendet werden';
      }
    }

    return '';
  };

  const isUserRowEmpty = (user) =>
    !trimValue(user.first_name) && !trimValue(user.last_name) && !trimValue(user.email) && !user.role;

  const isUserValid = (user) => {
    return ['first_name', 'last_name', 'email', 'role'].every(
      (fieldName) => !getUserFieldErrorMessage(user, fieldName, false)
    );
  };

  const handleAddUser = () => {
    const newUser = {
      id: `temp-${Date.now()}`,
      first_name: '',
      last_name: '',
      email: '',
      role: null,
      organization_id: currentUser?.organization_id,
      isNew: true
    };
    setLocalUsers([newUser, ...localUsers]);
  };

  const handleUpdateLocalUser = (userId, field, value) => {
    setLocalUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, [field]: value } : u)));
  };

  const handleDeleteLocalUser = (userId) => {
    setLocalUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleRequestDeleteLocalUser = (userId) => {
    // Never allow deleting yourself
    if (userId === currentUser?.id) return;

    const userToDelete = localUsers.find((u) => u.id === userId);

    // Newly added (not yet persisted) users can be removed immediately.
    if (userToDelete?.isNew) {
      handleDeleteLocalUser(userId);
      return;
    }

    // Existing DB users require confirmation.
    setUserIdPendingDelete(userId);
  };

  const handleCancelDeleteUser = () => setUserIdPendingDelete(null);

  const handleConfirmDeleteUser = () => {
    if (userIdPendingDelete) {
      handleDeleteLocalUser(userIdPendingDelete);
    }
    setUserIdPendingDelete(null);
  };

  const processUsersAndSave = (callback) => {
    // Drop empty new rows silently
    const filledUsers = localUsers.filter((user) => !(user.isNew && isUserRowEmpty(user)));

    if (filledUsers.some((user) => !isUserValid(user))) {
      setLocalUsers(filledUsers);
      setTouchedFields(touchAllFields(filledUsers, ['first_name', 'last_name', 'email', 'role']));
      return;
    }

    const deletedUsers = initialUsersState.filter(
      (initUser) => !filledUsers.find((locUser) => locUser.id === initUser.id)
    );

    const usersToProcess = filledUsers
      .map((user) => {
        if (user.isNew) return user;

        const initialUser = initialUsersState.find((u) => u.id === user.id);
        if (initialUser && !deepEqual(user, initialUser)) {
          return { ...user, isModified: true };
        }
        return null;
      })
      .filter(Boolean);

    if (deletedUsers.length === 0 && usersToProcess.length === 0) {
      callback?.();
      return;
    }

    bulkEditUsers({
      usersToProcess,
      deletedUsers,
      callback
    });
  };

  const handleOnBack = () =>
    processUsersAndSave(() => updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.APPEARANCE }));
  const handleOnNext = () =>
    processUsersAndSave(() => updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.DOCUMENTS }));

  const handleSaveAndLogout = () => {
    const filledUsers = localUsers.filter((user) => !(user.isNew && isUserRowEmpty(user)));
    const validUsers = filledUsers.filter(isUserValid);
    const hasChanges = !deepEqual(validUsers, initialUsersState);

    if (!hasChanges) {
      logout();
      return;
    }

    const deletedUsers = initialUsersState.filter((initUser) => !validUsers.find((u) => u.id === initUser.id));
    const usersToProcess = validUsers
      .map((user) => {
        if (user.isNew) return user;
        const initialUser = initialUsersState.find((u) => u.id === user.id);
        if (initialUser && !deepEqual(user, initialUser)) return { ...user, isModified: true };
        return null;
      })
      .filter(Boolean);

    if (deletedUsers.length === 0 && usersToProcess.length === 0) {
      logout();
      return;
    }

    bulkEditUsers({ usersToProcess, deletedUsers, callback: logout });
  };

  return (
    <>
      {userIdPendingDelete && (
        <ConfirmLeaveModal
          title="Nutzer löschen"
          message="Wollen Sie diesen Nutzer wirklich löschen?"
          cancelButtonLabel="Abbrechen"
          okButtonLabel="Nutzer löschen"
          onCancelButtonClick={handleCancelDeleteUser}
          onOkButtonClick={handleConfirmDeleteUser}
          open
        />
      )}
      <OnboardingNavbar onLogout={handleSaveAndLogout} loading={isLoading} />
      <OnboardingProgress />
      <OnboardingContent>
        <OnboardingStepWrapper>
          <OnboardingHeaderGroup>
            <OnboardingContentTitle>
              <Heading2>Rollen- und Rechtemanagement auf oqio</Heading2>
            </OnboardingContentTitle>
            <OnboardingContentInfo>
              <OnboardingContentGroupText>
                Bestimmen Sie, wer in Ihrem Unternehmen welche Befugnisse im Schaden- und Vertragsmanagement erhalten
                soll. Unterscheiden Sie zwischen den Rollen Sachbearbeiter, Manager und Admin.
              </OnboardingContentGroupText>
              <OnboardingContentGroupText>
                Registrierte Nutzer erhalten umgehend eine E-Mail für die persönliche Anmeldung in Ihrem Portal.
              </OnboardingContentGroupText>
              <OnboardingContentGroupLink href={ONBOARDING_HELP_CENTER_URL} target="_blank" rel="noopener noreferrer">
                Zur Rollen- und Rechteübersicht
              </OnboardingContentGroupLink>
            </OnboardingContentInfo>
          </OnboardingHeaderGroup>

          <OnboardingContentGroup className="step-users action">
            <Heading3>Nutzer Ihres Unternehmens</Heading3>
            <Button type="link" onClick={handleAddUser} icon={<IconCircledPlus />}>
              Hinzufügen
            </Button>
          </OnboardingContentGroup>

          {localUsers.length > 0 && (
            <OnboardingContentGroup className="step-users content-head">
              <OnboardingUserListHeader>
                <OnboardingUserListHeaderItem>Vorname</OnboardingUserListHeaderItem>
                <OnboardingUserListHeaderItem>Nachname</OnboardingUserListHeaderItem>
                <OnboardingUserListHeaderItem>E-Mail</OnboardingUserListHeaderItem>
                <OnboardingUserListHeaderItem>Rolle</OnboardingUserListHeaderItem>
                <OnboardingUserListHeaderItem></OnboardingUserListHeaderItem>
              </OnboardingUserListHeader>
            </OnboardingContentGroup>
          )}

          {localUsers.length > 0 && (
            <OnboardingContentGroup className="step-users content">
              {localUsers.map((user) => {
                const firstNameError = getUserFieldErrorMessage(user, 'first_name');
                const lastNameError = getUserFieldErrorMessage(user, 'last_name');
                const emailError = getUserFieldErrorMessage(user, 'email');
                const roleError = getUserFieldErrorMessage(user, 'role');

                return (
                  <OnboardingUserListRow key={user.id}>
                    <OnboardingFieldWrapper>
                      <Input
                        value={user.first_name}
                        onChange={(e) => handleUpdateLocalUser(user.id, 'first_name', e.target.value)}
                        onBlur={() => handleFieldBlur(user.id, 'first_name')}
                        status={firstNameError ? 'error' : undefined}
                      />
                      {firstNameError && <OnboardingFieldErrorText>{firstNameError}</OnboardingFieldErrorText>}
                    </OnboardingFieldWrapper>
                    <OnboardingFieldWrapper>
                      <Input
                        value={user.last_name}
                        onChange={(e) => handleUpdateLocalUser(user.id, 'last_name', e.target.value)}
                        onBlur={() => handleFieldBlur(user.id, 'last_name')}
                        status={lastNameError ? 'error' : undefined}
                      />
                      {lastNameError && <OnboardingFieldErrorText>{lastNameError}</OnboardingFieldErrorText>}
                    </OnboardingFieldWrapper>
                    <OnboardingFieldWrapper>
                      <Input
                        value={user.email}
                        onChange={(e) => handleUpdateLocalUser(user.id, 'email', e.target.value)}
                        onBlur={() => handleFieldBlur(user.id, 'email')}
                        status={emailError ? 'error' : undefined}
                        disabled={!user.isNew}
                      />
                      {emailError && <OnboardingFieldErrorText>{emailError}</OnboardingFieldErrorText>}
                    </OnboardingFieldWrapper>
                    <OnboardingFieldWrapper>
                      <Select
                        value={user.role}
                        onChange={(value) => handleUpdateLocalUser(user.id, 'role', value)}
                        onBlur={() => handleFieldBlur(user.id, 'role')}
                        options={userRoleOptions || []}
                        placeholder="Rolle wählen"
                        showCheckmark={false}
                        disabled={user.id === currentUser?.id}
                        status={roleError ? 'error' : undefined}
                      />
                      {roleError && <OnboardingFieldErrorText>{roleError}</OnboardingFieldErrorText>}
                    </OnboardingFieldWrapper>
                    <Button
                      type="link"
                      danger
                      icon={<IconTrash />}
                      disabled={user.id === currentUser?.id}
                      onClick={() => handleRequestDeleteLocalUser(user.id)}
                    />
                  </OnboardingUserListRow>
                );
              })}
            </OnboardingContentGroup>
          )}
        </OnboardingStepWrapper>
        <OnboardingActions onBack={handleOnBack} onNext={handleOnNext} loading={isLoading} />
      </OnboardingContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  users: selectUsers(state),
  currentUser: selectCurrentUser(state),
  userRoleOptions: selectUserRoleOptions(state),
  userOrganization: selectCurrentOrganization(state),
  updateConfigLoading: selectUpdateConfigLoading(state),
  bulkEditUsersLoading: selectBulkEditUsersLoading(state)
});

const mapDispatchToProps = {
  updateConfigOnboardingSettings,
  getUsers,
  logout,
  bulkEditUsers,
  getUserRoles
};

export const OnboardingStepUsers = connect(mapStateToProps, mapDispatchToProps)(OnboardingStepUsersComponent);
